import React, { useState, useEffect } from "react";
import OrderBoxIcon from "../../../assets/svg/orderbox.svg";
import search from "../../../assets/svg/search.svg";
import FillStartIcon from "../../../assets/svg/fill-star.svg";
import StartIcon from "../../../assets/svg/star.svg";
import ProductImage from "../../../assets/images/our-product.png";
import { GetAllOrder, GenerateInvoice } from "../../../Services/OrderService";
import { getImageUrl, handleImageError } from "../../../utils/imageUtils";
import { MoveFromOrderToCart } from "../../../Services/CartService";

const CancelOrder = () => {
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("Past 3 Months");
  const [loading, setLoading] = useState(true);
  const [loadingOrderItemId, setLoadingOrderItemId] = useState(null); // ✅ track loading button


  // Date filter options
  const dateFilterOptions = ["Past 3 Months", "2025", "2024", "2023"];

  // Get start and end dates based on filter
  const getDateRange = (filter) => {
    const now = new Date();
    let startDate, endDate;

    if (filter === "Past 3 Months") {
      startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      endDate = now;
    } else {
      // Assume it's a year like "2025"
      const year = parseInt(filter);
      startDate = new Date(year, 0, 1); // Jan 1
      endDate = new Date(year, 11, 31); // Dec 31
    }

    return {
      startDate: startDate.toLocaleDateString('en-CA'), // YYYY-MM-DD format
      endDate: endDate.toLocaleDateString('en-CA'),
    };
  };

  // Handle date filter change
  const handleDateFilterChange = (e) => {
    const newFilter = e.target.value;
    setDateFilter(newFilter);
  };

  // ✅ Fetch orders (used by both initial load & search)
  const fetchOrders = async (term = "", filter = dateFilter) => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      console.error("User ID not found");
      setLoading(false);
      return;
    }
    try {
      const params = { userId: parseInt(userId), orderStatus: 6 };
      if (term) params.filter = term;
      const { startDate, endDate } = getDateRange(filter);
      params.startDate = startDate;
      params.endDate = endDate;
      const response = await GetAllOrder(params);
      setOrders(response?.data?.orders || []); // handle null safely
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]); // fallback to empty
    } finally {
      setLoading(false);
    }
  };

  // ✅ Buy Again button handler
  const handleBuyAgain = async (orderItemId) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        console.error("User ID not found");
        setLoading(false);
        return;
      }

      setLoadingOrderItemId(orderItemId); // start loader on button

      const params = {
        orderItemId: parseInt(orderItemId),
        userId: userId,
      };

      const response = await MoveFromOrderToCart(params);
      if (response?.status) {
        // ✅ Update cart count in header
        window.dispatchEvent(new Event('cartUpdated'));
      }
    } catch (error) {
      console.error("Error moving item:", error);
    } finally {
      setLoadingOrderItemId(null); // stop loader
    }
  };

  // ✅ Initial Load
  useEffect(() => {
    fetchOrders();
  }, []);

  // ✅ Auto-search on input typing (with debounce)
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchOrders(searchTerm);
    }, 400); // wait 400ms after user stops typing
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  useEffect(() => {
    fetchOrders(searchTerm, dateFilter);
  }, [dateFilter]);

  return (
    <>
      <div className="main-content">
        <div className="d-flex align-items-center justify-content-between mb_28 order-content-box">
          <div className={"d-flex align-items-center justify-content-between gap-2"}>
            <h2 className="mb-0 fw_600 fs_24 lh-1">Your Orders</h2>
            <div className="drop-down-content d-md-none">
              <select
                className="form-select select-transparent"
                value={dateFilter}
                onChange={handleDateFilterChange}
              >
                {dateFilterOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={"form-input search-field"}>
            <input
              type="text"
              className="form-control"
              placeholder="Search Your Order"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <img src={search} alt="Search" className="search-icon" />
          </div>
        </div>

        <div className={"d-flex align-items-center justify-content-between gap-2 py_14 px_32 order-box mb_28"}>
          <div className={"d-flex align-items-center"}>
            <img src={OrderBoxIcon} alt="Order Icon" className="img-fluid" />
            <h5 className={"fs_18 fw_500 text-color lh-1 text-uppercase ml_10"}>
              {orders.length} Order{orders.length !== 1 ? "s" : ""} Cancelled
            </h5>
          </div>
          <div className="drop-down-content">
            <select
              className="form-select select-transparent"
              value={dateFilter}
              onChange={handleDateFilterChange}
            >
              {dateFilterOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ✅ If no orders found */}
        {loading ? (
          <div className="text-center py_50">
            <h5 className="fs_18 fw_400 text-muted">Fetching your orders...</h5>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py_50">
            <h5 className="fs_20 fw_500 text-muted">No Orders Found</h5>
          </div>
        ) : (
          <div className={"order-list-box"} style={{ overflowY: "auto", maxHeight: "400px" }}>
            {orders.map((order) => (
              <div key={order.orderId} className="order-card" style={{ backgroundColor: "#fcc3c9ff" }}>
                <div className="order-invoice d-flex align-items-center justify-content-between gap-2 mb_30">
                  <div className={"d-flex align-items-center delivery-details"}>
                    <div className={"order-name"}>
                      <p className={"fs_14 fw_400 lh-1 text-color mb_10"}>Order Delivered</p>
                      <h6 className={"fs_18 fw_500 lh-1 text-color mb-0"}>
                        {order.deliveryDate
                          ? new Date(order.deliveryDate).toLocaleDateString()
                          : "Not Delivered"}
                      </h6>
                    </div>

                    <div className={"order-name"}>
                      <p className={"fs_14 fw_400 lh-1 text-color mb_10"}>Total</p>
                      <h6 className={"fs_18 fw_500 lh-1 text-color mb-0"}>{order.totalPrice} INR</h6>
                    </div>

                    <div className={"order-name"}>
                      <p className={"fs_14 fw_400 lh-1 text-color mb_10"}>Ship To</p>
                      <h6 className={"fs_18 fw_500 lh-1 text-color mb-0"}>{order.shippingAddress}</h6>
                    </div>
                  </div>

                  {/* <div className="invoice-download text-end">
                    <p className={"fs_14 fw_400 lh-1 text-color mb_10"}>order # {order.orderId}</p>
                    <h6
                      className="fw_400 fs_16 lh-1 text-decoration-underline pe-auto"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleDownloadInvoice(order.orderId)}
                    >
                      Download Invoice
                    </h6>
                  </div> */}
                </div>

                <div className="order-grid-list">
                  {order.products.map((product) => (
                    <div
                      key={product.productId}
                      className="cart-grid d-flex align-items-center justify-content-between gap-2"
                    >
                      <div className={"d-flex align-items-start"}>
                        <div className="img-wrapper">
                          <img
                            className={"img-fluid"}
                            src={getImageUrl(product.path, ProductImage)}
                            onError={(e) => handleImageError(e, ProductImage)}
                            alt="cart product"
                          />
                        </div>
                        <div>
                          <div className={" mb_10"}>
                            <h6 className={"fs_24 fw_700 text-color mb-0"}>
                              {product.productName} {product.weight > 0 && `(${product.weight}KG)`}
                            </h6>
                          </div>
                          <div className={"tag-list d-flex align-items-center"}>
                            {product.attributes.subProducts.map((subProduct) => (
                              <div key={subProduct.id} className="tag py_10 px_10 fs_12 text-color">
                                {subProduct.name} {subProduct.weight}
                              </div>
                            ))}
                            {product.attributes.attributes.map((attr) => (
                              <div key={attr.id} className="tag py_10 px_10 fs_12 text-color">
                                {attr.value}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* ✅ Buy Again Button with Loader */}
                      <div>
                        <button
                          className="button btn-solid text-uppercase"
                          onClick={() => handleBuyAgain(product.orderItemId)}
                          disabled={loadingOrderItemId === product.productId}
                        >
                          {loadingOrderItemId === product.productId ? (
                            <div
                              className="spinner-border spinner-border-sm text-light"
                              role="status"
                            >
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          ) : (
                            "Add To Cart"
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default CancelOrder;